import { useState } from "react";
import { Archive, Download, FileUp, Pencil, Printer, RotateCcw, Send, ShieldCheck, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { printPdfBlob } from "@/lib/printPdf";
import { Button } from "@/components/ui/button";
import { ResourceFormDialog } from "./ResourceFormDialog";
import { SendResourceDialog } from "./SendResourceDialog";
import { ArchiveDialog, DecisionDialog, MarkReviewedDialog, SubmitReviewDialog, UploadVersionDialog } from "./ReviewDialogs";
import { errText, fetchBlob, isPdf, saveBlob } from "./resourceApi";

export async function downloadVersion(v) {
  try {
    saveBlob(await fetchBlob(`/clinical-resources/versions/${v.id}/file`, { download: true }), v.file.filename);
  } catch (e) {
    toast.error("Download failed", { description: errText(e) });
  }
}

async function printVersion(v) {
  try {
    const blob = await fetchBlob(`/clinical-resources/versions/${v.id}/file`, { preview: true });
    if (!isPdf(blob.type) && !isPdf(v.file.content_type) && !v.file.preview_path) return downloadVersion(v);
    await printPdfBlob(new Blob([blob], { type: "application/pdf" }));
  } catch (e) {
    toast.error("Print failed", { description: errText(e) });
  }
}

/** Permission-aware action bar for a resource; every action reloads the detail afterwards. */
export function ResourceActions({ data, meta, onChanged, decision, setDecision }) {
  const [dialog, setDialog] = useState(null);
  const res = data.resource;
  const p = meta.perms;
  const current = data.versions.find((v) => v.id === res.current_version_id);
  const latest = data.versions.find((v) => v.id === res.latest_version_id);
  const linkOnly = meta.types.find((t) => t.key === res.type)?.link_only;
  const archived = res.status === "archived";
  const canSubmit = res.can_edit && !archived && (latest?.status === "draft" || (linkOnly && res.pending_version_status !== "in_review"));
  const close = () => setDialog(null);
  const done = () => { setDialog(null); setDecision(null); onChanged(); };
  const btn = "h-8";
  return (
    <div className="flex flex-wrap gap-2" data-testid="resource-actions">
      {res.can_send && <Button size="sm" className={btn} onClick={() => setDialog("send")} data-testid="resource-action-send"><Send className="h-3.5 w-3.5 mr-1" /> Send to a client</Button>}
      {current?.file && <Button size="sm" variant="outline" className={btn} onClick={() => printVersion(current)} data-testid="resource-action-print"><Printer className="h-3.5 w-3.5 mr-1" /> Print</Button>}
      {current?.file && <Button size="sm" variant="outline" className={btn} onClick={() => downloadVersion(current)} data-testid="resource-action-download"><Download className="h-3.5 w-3.5 mr-1" /> Download</Button>}
      {res.can_edit && !archived && <Button size="sm" variant="outline" className={btn} onClick={() => setDialog("edit")} data-testid="resource-action-edit"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit details</Button>}
      {res.can_edit && !archived && !linkOnly && <Button size="sm" variant="outline" className={btn} onClick={() => setDialog("upload")} data-testid="resource-action-upload"><FileUp className="h-3.5 w-3.5 mr-1" /> Upload new version</Button>}
      {canSubmit && <Button size="sm" variant="outline" className={btn} onClick={() => setDialog("submit")} data-testid="resource-action-submit"><ShieldCheck className="h-3.5 w-3.5 mr-1" /> Send for review</Button>}
      {p.approve && current && !archived && <Button size="sm" variant="outline" className={btn} onClick={() => setDialog("reviewed")} data-testid="resource-action-mark-reviewed"><ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Confirm review</Button>}
      {p.manage && !archived && <Button size="sm" variant="outline" className={`${btn} text-red-700`} onClick={() => setDialog("archive")} data-testid="resource-action-archive"><Archive className="h-3.5 w-3.5 mr-1" /> Archive</Button>}
      {p.manage && archived && (
        <Button size="sm" variant="outline" className={btn} data-testid="resource-action-restore"
                onClick={() => api.post(`/clinical-resources/${res.id}/restore`).then(() => { toast.success("Restored"); onChanged(); }).catch((e) => toast.error("Restore failed", { description: errText(e) }))}>
          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore</Button>
      )}

      {dialog === "send" && <SendResourceDialog resource={res} source={{ kind: "library" }} onClose={close} onSent={onChanged} />}
      {dialog === "edit" && <ResourceFormDialog meta={meta} resource={res} onClose={close} onSaved={done} />}
      {dialog === "upload" && <UploadVersionDialog resource={res} onClose={close} onDone={done} />}
      {dialog === "submit" && <SubmitReviewDialog resource={res} onClose={close} onDone={done} />}
      {dialog === "reviewed" && <MarkReviewedDialog resource={res} onClose={close} onDone={done} />}
      {dialog === "archive" && <ArchiveDialog resource={res} onClose={close} onDone={done} />}
      {decision && <DecisionDialog resource={res} version={decision.version} decision={decision.kind} onClose={() => setDecision(null)} onDone={done} />}
    </div>
  );
}
